import { Button, Heading, Text } from "@instacover/ui"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useRef } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useTimeoutFn } from "react-use"

import { getSessionDetailOptions } from "@/api/hooks"
import { ExtraIcons } from "@/components/icons/InstructionImageType"
import { ImageLayout } from "@/components/layouts/ImageLayout"
import { useDispatchSDKEvent } from "@/helpers/sdk"
import { useSessionDetail } from "@/services/dexie"
import { queryClient } from "@/services/tanstackQuery/queryClient"

export const Route = createFileRoute("/$sessionId/_session/finished")({
  loader: async ({ params }) => {
    // check if session is truly finished on server (ic-3245)
    const sessionDetail = await queryClient.ensureQueryData(getSessionDetailOptions(params.sessionId))
    if (!sessionDetail.finished) {
      throw redirect({ to: "/$sessionId/final-check", params: { sessionId: params.sessionId }, replace: true })
    }
  },
  component: FinishedPage,
})

function FinishedPage() {
  useDispatchSDKEvent("finished")

  const { t } = useTranslation()
  const { sessionId } = Route.useParams()
  const { data: detail } = useSessionDetail(sessionId)

  const redirectUrlOnSessionFinish = detail?.redirectUrlOnSessionFinish
  const redirectedRef = useRef(false)

  useTimeoutFn(async () => {
    if (!redirectUrlOnSessionFinish || redirectedRef.current) {
      return
    }

    window.location.href = redirectUrlOnSessionFinish
    redirectedRef.current = true
  }, 2000)

  return (
    <ImageLayout
      dataTestId="finished-page"
      baseLayoutProps={{ hideCounter: true }}
      iconName={ExtraIcons.Sent}
      content={
        <>
          <Heading as="h2" variant="h1" className="text-center">
            {t("finished.sent")}
          </Heading>

          <div className="h-6 w-6 shrink-0" />

          <Text as="p" className="text-left">
            <Trans i18nKey="finished.thankyou" />
          </Text>

          <div className="h-6 w-6 shrink-0" />

          {redirectUrlOnSessionFinish && (
            <Button as="a" href={redirectUrlOnSessionFinish} variant="primary" className="mb-6">
              {t("finished.close")}
            </Button>
          )}
        </>
      }
    />
  )
}
